import { Printer } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import {
  HandoutPageRule,
  HandoutSheet,
  whenImagesReady,
} from "../manual/HandoutSheet";
import { classHandoutPath, classPath, useNavigate } from "../router";

import type { ApiErrorCode } from "../api/client";
import type { ClassRecord, StudentAccount } from "@canvaschool/contracts";

type Loaded = { classRecord: ClassRecord; students: StudentAccount[] };

/**
 * 학급 학생마다 아이디를 채운 안내문을 번호 순서로 한 장씩 찍는다. 사용 중지한
 * 계정은 로그인할 수 없으니 뺀다. `autoPrint`면 그림이 다 들어온 뒤 인쇄 창을
 * 한 번 연다.
 */
export const ClassHandoutPage = ({
  classId,
  autoPrint = false,
}: {
  classId: string;
  autoPrint?: boolean;
}) => {
  const auth = useAuth();
  const { api } = auth;
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<ApiErrorCode | null>(null);
  const [preparing, setPreparing] = useState(false);
  const sheetsRef = useRef<HTMLDivElement>(null);
  const autoPrinted = useRef(false);

  useEffect(() => {
    let live = true;
    void Promise.all([api.getClass(classId), api.getRoster(classId)]).then(
      ([classRecord, roster]) => {
        if (live) {
          setLoaded({
            classRecord,
            students: roster
              .filter((student) => student.enabled)
              .sort((a, b) => a.studentNumber - b.studentNumber),
          });
        }
      },
      (failure) => {
        if (live) {
          setLoadError(auth.handleError(failure));
        }
      },
    );
    return () => {
      live = false;
    };
    // `auth` changes identity on every session update; its handler is stable in effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId]);

  const print = async () => {
    if (!sheetsRef.current) {
      return;
    }
    setPreparing(true);
    try {
      await whenImagesReady(sheetsRef.current);
    } finally {
      setPreparing(false);
    }
    window.print();
  };

  const hasSheets = Boolean(loaded?.students.length);
  useEffect(() => {
    if (!autoPrint || !hasSheets || autoPrinted.current) {
      return;
    }
    autoPrinted.current = true;
    // 새로고침이나 뒤로 가기로 돌아와도 인쇄 창이 다시 뜨지 않게 한다.
    navigate(classHandoutPath(classId), { replace: true });
    void print();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, hasSheets, classId]);

  const back = <Link to={classPath(classId, "students")}>← 학생 계정으로</Link>;

  if (loadError) {
    return (
      <div className="canvaschool-handout">
        <div className="ch-toolbar">{back}</div>
        <ErrorAlert code={loadError} />
      </div>
    );
  }
  if (!loaded) {
    return <p aria-busy="true">불러오는 중…</p>;
  }
  const { classRecord, students } = loaded;

  return (
    <div className="canvaschool-handout">
      <HandoutPageRule />
      <div className="ch-toolbar">
        {back}
        <p className="ch-toolbar-note" role="status">
          {students.length > 0
            ? `${classRecord.name} 학생 ${students.length}명 · A4 ${students.length}장`
            : "인쇄할 학생 계정이 없습니다."}
        </p>
        <button
          type="button"
          className="canvaschool-btn-primary"
          disabled={students.length === 0 || preparing}
          onClick={() => void print()}
        >
          <Printer size={18} aria-hidden="true" />
          {preparing ? "그림 불러오는 중…" : "인쇄하기"}
        </button>
      </div>

      <div className="ch-sheets" ref={sheetsRef}>
        {students.map((student) => (
          <HandoutSheet
            key={student.userId}
            owner={`${classRecord.name} ${student.studentNumber}번`}
            loginId={student.loginId}
            password={student.initialPassword ?? "바꾼 비밀번호"}
          />
        ))}
      </div>
    </div>
  );
};
